// The loading screen (owner 2026-10-18; moved out of an inline <script> in index.html by WS-23, so the CSP needs no
// 'unsafe-inline' for scripts; redesigned to the owner's picks of 2026-11-05). Runs before anything else of the engine:
//   * its look: the client's choice on the portal's Map style page, one for all their maps, pinned by the host as
//     the boot meta `mapmystay-loader` (worker/store.js LoaderLook): a picture (a public image key, served under the
//     page's `mapmystay-objects` root; the smaller one on a narrow screen), the veil's colour and strength, a slow
//     zoom, the name, the logo and the height lines; the studio's `mark` (our logo instead, or both). On draft hosts
//     `?loader=<json>` previews one (it may also name a same-origin `url`).
//   * no twitch: the words show once their own font is in (at most 1.2 s), the logo when its file is in.
//   * the logo: an SVG drawn for dark grounds draws itself (its outline per part, then its fill: "Draw for the logo
//     too"); a picture, or a logo on its white disc, fades in. Read from its file as data: only its viewBox and its
//     paths' outlines, fill rules and colours are kept, built anew (nothing of the file runs or is shown as it is).
//   * the progress: main.js dispatches 'map-progress' events while the package loads: the comet bar, the step's words
//     and the percentage.
//   * "Fine survey": faint height lines behind, on a computer with a mouse alone; the cursor bends the lines near it
//     aside and lights them in the bar colour. Drawn only while the cursor moves; gone once the map is ready.
//   * VIS-206: a failed start ('map-failed', from js/boot.js): the reason's words and buttons (see below).
(() => {
  const l = document.getElementById('loader');
  if (!l) return;
  const meta = (n) => document.querySelector(`meta[name="mapmystay-${n}"]`)?.content || '';
  const drafts = meta('env') !== 'production', q = new URLSearchParams(location.search);
  // VIS-206: the words of the cover and of a failed start come before the runtime knows the page's language: ?lang=,
  // else the browser's; English or Finnish (the product's languages). The other parts below read it from here.
  l.dataset.lang = /^fi\b/i.test(new URLSearchParams(location.search).get('lang') || navigator.language || '') ? 'fi' : 'en';
  const fi = l.dataset.lang === 'fi';
  const style = meta('style') || (drafts && q.get('style')) || '';
  if (/^(float|dock|island|ivory|lantern|strip|frost)$/.test(style)) l.dataset.style = style;
  let look = null;
  try { look = JSON.parse((drafts && q.get('loader')) || meta('loader') || 'null'); } catch { /* errors:ignore: not JSON */ }
  look = look && typeof look === 'object' ? look : {};
  // VIS-208: the client's own colours (boot meta mapmystay-colors, worker/store.js BrandColors): the bar and rule in the
  // button colour, the ground in the panel colour unless the loading screen has a colour of its own
  let colors = null;
  try { colors = JSON.parse(meta('colors') || 'null'); } catch { /* errors:ignore: not JSON */ }
  if (/^#[0-9a-f]{6}$/i.test(colors?.accent)) l.style.setProperty('--ld-accent', colors.accent);
  if (/^#[0-9a-f]{6}$/i.test(colors?.ink)) l.style.setProperty('--ld-tint', colors.ink);
  if (/^#[0-9a-f]{6}$/i.test(look.tint)) l.style.setProperty('--ld-tint', look.tint);
  if (typeof look.dim === 'number') l.style.setProperty('--ld-dim', String(Math.min(0.9, Math.max(0, look.dim))));
  l.classList.toggle('no-motion', look.motion === false);
  l.classList.toggle('no-name', look.name === false);
  l.classList.toggle('no-logo', look.logo === false);
  const mark = look.mark === 'mms' || look.mark === 'both' ? look.mark : ''; // the studio's: our logo instead of the client's, or both
  const KEY = /^a\/map_[0-9a-hjkmnp-tv-z]{16}\/ast_[0-9a-hjkmnp-tv-z]{16}\/[1-9][0-9]{1,4}\.webp$/;
  // VIS-206: a website embed starts as its cover (boot meta mapmystay-cover, worker/store.js CoverLook; ?cover=<json> on
  // draft hosts): its own picture first, else the loading picture; its brand colours; the button's words. `open=1` = the
  // map opened in a tab of its own from a cover: no cover.
  let cover = null;
  try { cover = JSON.parse((drafts && q.get('cover')) || meta('cover') || 'null'); } catch { /* errors:ignore: not JSON */ }
  if (!cover || typeof cover !== 'object' || q.get('open') === '1') cover = null;
  const own = (o) => (innerWidth * (devicePixelRatio || 1) <= 1000 && KEY.test(o?.imageSm) && o.imageSm) || (KEY.test(o?.image) && o.image);
  const key = own(cover) || own(look);
  const objects = meta('objects');
  const draftUrl = (u) => (drafts && typeof u === 'string' && !/^[a-z]+:|^\/\//i.test(u) ? u : '');
  const src = key && objects ? objects + key : draftUrl(cover?.url) || draftUrl(look.url);
  if (cover) {
    const brand = cover.brand && typeof cover.brand === 'object' ? cover.brand : {};
    if (/^#[0-9a-f]{6}$/i.test(brand.accent)) l.style.setProperty('--ld-accent', brand.accent);
    if (/^#[0-9a-f]{6}$/i.test(brand.ink)) l.style.setProperty('--cv-ink', brand.ink);
    const img = l.querySelector('img');
    if (!img.getAttribute('src') && typeof brand.logo === 'string' && /^[\w./-]+\.(svg|png|webp)$/i.test(brand.logo) && !brand.logo.includes('..')) img.src = brand.logo; // relative to <base>, the asset root
    const label = cover.label && typeof cover.label === 'object' ? cover.label[fi ? 'fi' : 'en'] : '';
    l.querySelector('.ld-label').textContent = typeof label === 'string' && label.trim() ? label.trim().slice(0, 40) : fi ? 'Tutustu hotelliin' : 'Explore our hotel';
    l.querySelector('.ld-o-t').textContent = fi ? 'Kartta latautuu' : 'Loading the map';
    l.dataset.state = 'cover'; // js/boot.js waits for the click ('map-open') before it fetches the map
    l.querySelector('.ld-cover').hidden = false;
  }
  if (src) {
    const pic = new Image();
    pic.onload = () => { l.querySelector('.ld-pic').style.backgroundImage = `url("${pic.src}")`; l.classList.add('has-pic'); };
    pic.src = src;
  }

  // ---- the logo (owner's pick 2026-11-05: Draw, for every client's SVG logo)
  const box = l.querySelector('.ld-logo'), logo = box.querySelector('img'), h = l.querySelector('h2');
  const NS = 'http://www.w3.org/2000/svg';
  const make = (name, attrs = {}) => { const e = document.createElementNS(NS, name); for (const k of Object.keys(attrs)) e.setAttribute(k, attrs[k]); return e; };
  const NUMS = /^[\d\s,.eE+-]+$/, OUTLINE = /^[MmLlHhVvCcSsQqTtAaZz\d\s,.eE+-]+$/, HEX = /^#(?:[0-9a-f]{3}){1,2}$/i;
  const files = new Map();
  /** An SVG file of this site, read as data: its viewBox and its paths (outline, fill rule, colour); null = not one. */
  const readSvg = (url) => {
    const u = new URL(url, document.baseURI);
    if (u.origin !== location.origin || !/\.svg$/i.test(u.pathname)) return Promise.resolve(null);
    if (!files.has(u.href)) files.set(u.href, fetch(u.href, { priority: 'low' }).then((r) => (r.ok ? r.text() : '')).then((text) => {
      const doc = new DOMParser().parseFromString(text, 'image/svg+xml'), root = doc.documentElement;
      const vb = root.localName === 'svg' ? (root.getAttribute('viewBox') || '').trim() : '';
      const paths = [...doc.getElementsByTagNameNS(NS, 'path')].map((p) => ({ d: (p.getAttribute('d') || '').trim(), rule: p.getAttribute('fill-rule') === 'evenodd' ? 'evenodd' : 'nonzero', fill: HEX.test(p.getAttribute('fill') || '') ? p.getAttribute('fill') : '' }));
      const ok = paths.filter((p) => p.d && OUTLINE.test(p.d));
      return NUMS.test(vb) && vb.split(/[\s,]+/).length === 4 && ok.length ? { vb, paths: ok } : null;
    }).catch(() => null)); // a file that cannot be read draws nothing: the picture fades in instead
    return files.get(u.href);
  };
  // a path's parts, one stroke each (split at its absolute moves; a path with a relative move after the first stays whole)
  const parts = (d) => (/m/.test(d.slice(1)) ? [d] : d.split(/(?=M)/).map((s) => s.trim()).filter(Boolean));
  /** The logo as an inline SVG: the outline that draws itself, the fill, the sheen. `vb` overrides the file's own. */
  function drawn(svg, { paths = svg.paths, vb = svg.vb, cls = '' } = {}) {
    const [, , w, ht] = vb.split(/[\s,]+/).map(Number);
    const colour = paths.find((p) => p.fill)?.fill || '#f3f5f8';
    const lg = document.createElement('span');
    lg.className = `ld-lg${w > ht * 2.5 ? ' is-wide' : ''}${cls}`;
    lg.style.setProperty('--lc', colour);
    lg.style.setProperty('--sw', String(Math.round(ht * 14) / 1000)); // about 1.4 % of its drawn height
    const base = make('svg', { viewBox: vb, 'aria-hidden': 'true', focusable: 'false' }), ds = make('g', { class: 'ld-ds' }), fl = make('g', { class: 'ld-fl' });
    let i = 0;
    for (const p of paths) {
      for (const d of parts(p.d)) { const s = make('path', { d, pathLength: '1' }); s.style.setProperty('--i', String(i++)); ds.append(s); }
      fl.append(make('path', { d: p.d, 'fill-rule': p.rule, fill: p.fill || colour }));
    }
    base.append(ds, fl);
    const sh = make('svg', { viewBox: vb, class: 'ld-sh', 'aria-hidden': 'true', focusable: 'false' });
    for (const p of paths) sh.append(make('path', { d: p.d, 'fill-rule': p.rule }));
    lg.append(base, sh);
    return lg;
  }
  // our mark: the first path of css/mms-logo.svg (the wordmark is the second), in a box measured round it
  const ours = () => readSvg('css/mms-logo.svg');
  const markBox = (svg) => {
    const probe = make('svg', { viewBox: svg.vb, width: '0', height: '0', style: 'position:absolute' }), p = make('path', { d: svg.paths[0].d });
    probe.append(p); document.body.append(probe);
    const b = p.getBBox(), pad = Math.max(b.width, b.height) * 0.02;
    probe.remove();
    return [b.x - pad, b.y - pad, b.width + 2 * pad, b.height + 2 * pad].map((n) => Math.round(n * 100) / 100).join(' ');
  };
  const sep = document.createElement('i'); sep.className = 'ld-sep'; sep.hidden = true;
  // the client's logo: drawn when it is an SVG drawn for dark grounds (a logo on its white disc keeps the disc), else the picture
  let took = '', reading = false;
  const clientLogo = async () => {
    const at = logo.getAttribute('src') || '';
    if (!at || at === took || mark === 'mms') return;
    took = at;
    reading = true;
    const svg = /\.svg(?:$|\?)/i.test(at) && (l.dataset.logo === 'bare' || document.documentElement.dataset.logo === 'bare') ? await readSvg(logo.src) : null;
    if (took !== at) return; // another file meanwhile
    reading = false;
    box.querySelector('.ld-lg:not(.is-ours)')?.remove();
    if (svg) { logo.hidden = true; box.prepend(drawn(svg)); } else { logo.hidden = false; if (logo.complete && logo.naturalWidth) logo.classList.add('is-in'); }
    sep.hidden = false;
  };
  logo.addEventListener('load', () => { if (!logo.hidden && !reading) logo.classList.add('is-in'); }); // (an SVG waits: it may draw itself instead)
  new MutationObserver(clientLogo).observe(logo, { attributes: true, attributeFilter: ['src'] }); // config.js sets it once the package is read
  clientLogo();
  if (mark === 'mms') logo.hidden = true;
  if (mark && !cover) ours().then((svg) => {
    if (!svg) return;
    if (mark === 'mms') box.append(drawn(svg, { cls: ' is-ours' })); // our whole logo, the wordmark too
    else box.append(sep, drawn(svg, { paths: svg.paths.slice(0, 1), vb: markBox(svg), cls: ' is-ours' }));
  });
  // "3D map made by [our mark] Map My Stay" (the info box's credit.made words), unless the logo already is ours
  const credit = l.querySelector('.ld-credit');
  if (credit && !cover && !(mark === 'mms' && look.logo !== false)) ours().then((svg) => {
    if (!svg) return;
    const made = credit.firstElementChild;
    if (fi) made.textContent = '3D-kartan teki';
    const m = make('svg', { viewBox: markBox(svg), focusable: 'false' });
    m.append(make('path', { d: svg.paths[0].d, 'fill-rule': svg.paths[0].rule }));
    credit.querySelector('.ld-mark').append(m);
    credit.hidden = false;
  });

  // ---- the words and the number: the words of the first step until main.js names one
  const wbox = l.querySelector('.ld-wbox'), num = l.querySelector('.ld-num b'), say = l.querySelector('.ld-say');
  const first = fi ? 'Kartta latautuu' : 'Loading the map';
  say.textContent = first;
  let shown = 0, target = 0, raf = 0, sized = '';
  const size = (all) => { // every step's words stacked unseen in one cell: the box is as wide as the widest
    const key = all.join('\n');
    if (key === sized) return;
    sized = key;
    for (const s of wbox.querySelectorAll('.ld-sizer')) s.remove();
    for (const w of all) { const s = document.createElement('i'); s.className = 'ld-sizer'; s.textContent = w; wbox.prepend(s); }
  };
  const word = (t) => {
    const now = wbox.querySelector('span:not(.out)');
    if (now?.textContent === t) return;
    for (const o of wbox.querySelectorAll('span:not(.out)')) { o.classList.add('out'); setTimeout(() => o.remove(), 750); }
    const n = document.createElement('span'); n.className = 'pre'; n.textContent = t; wbox.append(n);
    requestAnimationFrame(() => requestAnimationFrame(() => n.classList.remove('pre')));
  };
  size([first]); word(first);
  const count = () => { // the number runs up to the step's share in 0.6 s, as the bar does
    cancelAnimationFrame(raf);
    const from = shown, t0 = performance.now();
    const step = (now) => { const k = Math.min(1, (now - t0) / 600); shown = from + (target - from) * (1 - (1 - k) ** 3); num.textContent = String(Math.round(shown * 100)); if (k < 1) raf = requestAnimationFrame(step); };
    raf = requestAnimationFrame(step);
  };
  // demo loader B (owner, 2026-10-25): the step's warm words (main.js `words`, js/stepwords.js; `steps`: every step's,
  // for the box's width) and, on the rescue set (`lighter`), one line above the bar the whole time, the same as the
  // website's cover says
  addEventListener('map-progress', (e) => {
    const words = e.detail.words || e.detail.msg, p = Math.min(1, Math.max(0, Number(e.detail.p) || 0));
    l.style.setProperty('--p', String(p));
    if (Array.isArray(e.detail.steps)) size(e.detail.steps.filter((w) => typeof w === 'string'));
    say.textContent = words;
    word(words);
    if (p !== target) { target = p; count(); }
    const o = l.querySelector('.ld-o-msg'); // VIS-206: the cover's loading pill says the same
    if (o) o.textContent = words;
    const lite = l.querySelector('.ld-lighter');
    if (lite && e.detail.lighter && lite.hidden) {
      // "phone" only on a phone (errors.js deviceFacts: an iPhone or Android device, its short side at most 500): never by
      // the window's width, which inside a website's frame is the frame's (a MacBook read "this phone", owner 2026-10-29)
      const phone = /iPhone|iPod|Android/.test(navigator.userAgent) && Math.min(screen.width, screen.height) <= 500;
      lite.textContent = fi ? `Ladataan kevyempää versiota tälle ${phone ? 'puhelimelle' : 'laitteelle'}` : `Loading a lighter version for this ${phone ? 'phone' : 'device'}`;
      lite.hidden = false;
    }
  });

  // ---- "Fine survey" (owner's pick 2026-11-05): the height lines of a made-up landscape (a noise field, marching
  // squares joined into whole lines, softened, drawn as curves through their points). They never move by themselves; a
  // mouse near them pushes them aside (a soft Gaussian bend, so every line stays smooth) and lights them in the bar
  // colour. Computers with a mouse alone, in the wide layout (css "Horizon"), and not under a website cover; dropped with
  // its listeners once the map is ready.
  // Smooth while the map loads (owner, 2026-11-05: "VERY LAGGY"): redrawing the lines as canvas paths every frame the
  // mouse moved halved the map's frame rate while it loaded and opened it seconds later. Now the lines are built in small
  // slices whenever the page has a moment, drawn ONCE into a still picture (the thin lines in its red channel, the heavy
  // ones in green), and a tiny WebGL program shows that picture, bent round the cursor and lit in the bar colour: per
  // frame one triangle, and frames only while the cursor moves or its light fades. No WebGL: the still lines, unbent.
  const WIDE = matchMedia('(min-width: 640px) and (min-aspect-ratio: 1/1)'), MOUSE = matchMedia('(hover: hover) and (pointer: fine)');
  const VS = 'attribute vec2 p;varying vec2 v;void main(){v=vec2(p.x*.5+.5,.5-p.y*.5);gl_Position=vec4(p,0.,1.);}';
  const FS = '#ifdef GL_FRAGMENT_PRECISION_HIGH\nprecision highp float;\n#else\nprecision mediump float;\n#endif\n'
    + 'varying vec2 v;uniform sampler2D tex;uniform vec2 res,c;uniform float push,sig2,on,R;uniform vec3 col;'
    + 'void main(){vec2 q=v*res,d=q-c;float d2=dot(d,d),g=exp(-d2/sig2)*push;' // the bend: sample nearer the cursor
    + 'vec4 m=texture2D(tex,(q-(d2>1e-4?d*inversesqrt(d2)*g:vec2(0.)))/res);'
    + 'float base=m.r*.022+m.g*.048,k=sqrt(d2)/R,a=k<.45?mix(.32,.1,k/.45):mix(.1,0.,clamp((k-.45)/.55,0.,1.)),lit=a*on*min(1.,m.r+m.g);'
    + 'gl_FragColor=vec4(vec3(base)+col*lit,base+lit);}'; // premultiplied: white lines plus the light in the bar colour
  function lines() {
    if (look.lines === false || cover || !MOUSE.matches || document.body.classList.contains('ready')) return;
    const cv = document.createElement('canvas');
    cv.className = 'ld-lines'; cv.setAttribute('aria-hidden', 'true');
    l.querySelector('.ld-veil').after(cv);
    const accent = (() => { // the bar colour as 0..1 numbers (a canvas reads any CSS colour into #rrggbb)
      const c = document.createElement('canvas').getContext('2d');
      if (c) c.fillStyle = getComputedStyle(l).getPropertyValue('--ld-accent').trim() || '#d2b46e';
      const n = c && /^#[0-9a-f]{6}$/i.test(c.fillStyle) ? parseInt(c.fillStyle.slice(1), 16) : 0xd2b46e;
      return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];
    })();
    let gl = null, U = null, tried = false, flat = null, t = null, loop = 0, job = 0, gen = 0, rebuild = 0;
    const ptr = { x: 0, y: 0, in: false };
    const idle = (fn) => (window.requestIdleCallback ? requestIdleCallback(fn, { timeout: 400 }) : setTimeout(() => fn({ timeRemaining: () => 6 }), 16));
    const unidle = (id) => (window.cancelIdleCallback ? cancelIdleCallback(id) : clearTimeout(id));
    const setup = () => {
      tried = true;
      gl = cv.getContext('webgl', { alpha: true, premultipliedAlpha: true, antialias: false, depth: false, stencil: false });
      if (!gl) return false;
      const sh = (type, src) => { const s = gl.createShader(type); gl.shaderSource(s, src); gl.compileShader(s); return s; };
      const prog = gl.createProgram();
      gl.attachShader(prog, sh(gl.VERTEX_SHADER, VS)); gl.attachShader(prog, sh(gl.FRAGMENT_SHADER, FS)); gl.linkProgram(prog);
      if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) { gl = null; return false; }
      gl.useProgram(prog);
      gl.bindBuffer(gl.ARRAY_BUFFER, gl.createBuffer());
      gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW); // one triangle over the screen
      const at = gl.getAttribLocation(prog, 'p');
      gl.enableVertexAttribArray(at); gl.vertexAttribPointer(at, 2, gl.FLOAT, false, 0, 0);
      gl.bindTexture(gl.TEXTURE_2D, gl.createTexture());
      for (const k of [gl.TEXTURE_MIN_FILTER, gl.TEXTURE_MAG_FILTER]) gl.texParameteri(gl.TEXTURE_2D, k, gl.LINEAR);
      for (const k of [gl.TEXTURE_WRAP_S, gl.TEXTURE_WRAP_T]) gl.texParameteri(gl.TEXTURE_2D, k, gl.CLAMP_TO_EDGE);
      gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, true); // a channel = how much of a line covers the pixel
      U = Object.fromEntries(['res', 'c', 'push', 'sig2', 'on', 'R', 'col'].map((n) => [n, gl.getUniformLocation(prog, n)]));
      cv.addEventListener('webglcontextlost', (e) => { e.preventDefault(); gl = null; t = null; cv.remove(); });
      return true;
    };
    const build = () => { // the lines, a level at a time, each slice when the page has a moment
      const my = ++gen;
      unidle(job); cancelAnimationFrame(loop); loop = 0; t = null;
      const w = innerWidth, ht = innerHeight;
      if (!WIDE.matches || !w || !ht) { cv.classList.remove('is-in'); return; }
      const dpr = Math.min(devicePixelRatio || 1, 1.75), still = document.createElement('canvas');
      still.width = Math.round(w * dpr); still.height = Math.round(ht * dpr);
      const sg = still.getContext('2d');
      if (!sg) return;
      sg.setTransform(dpr, 0, 0, dpr, 0, 0); sg.lineCap = 'round'; sg.lineJoin = 'round'; sg.globalCompositeOperation = 'lighter';
      const levels = contours(terrain(w, ht), w, ht, 32, 5);
      const step = (deadline) => {
        if (my !== gen) return;
        do {
          const next = levels.next();
          if (next.done) { show(still, w, ht); return; }
          if (!next.value) continue; // the field is sampled
          const { heavy, lines: ls } = next.value, p = new Path2D();
          for (const L of ls) trace(p, L.pts, L.closed);
          sg.strokeStyle = heavy ? '#00ff00' : '#ff0000'; sg.lineWidth = heavy ? 0.9 : 0.5; sg.stroke(p);
        } while (deadline.timeRemaining() > 4);
        job = idle(step);
      };
      job = idle(step);
    };
    const show = (still, w, ht) => {
      cv.width = still.width; cv.height = still.height;
      if (gl || (!tried && setup())) {
        gl.viewport(0, 0, cv.width, cv.height);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, still);
        const D = Math.hypot(w, ht);
        gl.uniform2f(U.res, w, ht); gl.uniform1f(U.sig2, 2 * (0.09 * D) ** 2); gl.uniform1f(U.R, 0.19 * D); gl.uniform3f(U.col, ...accent);
        t = { w, h: ht, cx: w / 2, cy: ht / 2, tx: w / 2, ty: ht / 2, on: 0, fresh: true };
        paint();
      } else { // no WebGL: the still lines in white, unbent
        flat = flat || cv.getContext('2d');
        if (!flat) return;
        flat.globalCompositeOperation = 'source-over'; flat.clearRect(0, 0, cv.width, cv.height);
        flat.globalAlpha = 0.035; flat.drawImage(still, 0, 0); flat.globalAlpha = 1;
        flat.globalCompositeOperation = 'source-in'; flat.fillStyle = '#fff'; flat.fillRect(0, 0, cv.width, cv.height);
      }
      cv.classList.add('is-in');
    };
    const paint = () => { // one frame: where the cursor is, how strongly it bends and lights
      loop = 0;
      if (!t || !gl) return;
      const want = ptr.in ? 1 : 0;
      if (want) { t.tx = ptr.x; t.ty = ptr.y; if (t.on < 0.01) { t.cx = t.tx; t.cy = t.ty; } }
      const dOn = (want - t.on) * 0.05;
      const settled = Math.abs(t.tx - t.cx) < 0.2 && Math.abs(t.ty - t.cy) < 0.2 && Math.abs(dOn) < 0.001;
      if (settled && !t.fresh) return; // nothing changes: no more frames
      t.fresh = false;
      t.cx += (t.tx - t.cx) * 0.1; t.cy += (t.ty - t.cy) * 0.1; t.on = want || t.on > 0.003 ? t.on + dOn : 0;
      gl.uniform2f(U.c, t.cx, t.cy); gl.uniform1f(U.push, 11 * t.on); gl.uniform1f(U.on, t.on);
      gl.clearColor(0, 0, 0, 0); gl.clear(gl.COLOR_BUFFER_BIT); gl.drawArrays(gl.TRIANGLES, 0, 3);
      if (!settled) loop = requestAnimationFrame(paint);
    };
    const wake = () => { if (t && gl && !loop) loop = requestAnimationFrame(paint); };
    const move = (e) => { if (e.pointerType !== 'mouse') return; ptr.x = e.clientX; ptr.y = e.clientY; ptr.in = true; wake(); };
    const leave = () => { ptr.in = false; wake(); };
    const resize = () => { clearTimeout(rebuild); rebuild = setTimeout(build, 250); };
    l.addEventListener('pointermove', move);
    l.addEventListener('pointerleave', leave);
    addEventListener('resize', resize);
    const stop = new MutationObserver(() => {
      if (!document.body.classList.contains('ready')) return;
      stop.disconnect(); gen++; unidle(job); cancelAnimationFrame(loop); clearTimeout(rebuild); t = null;
      l.removeEventListener('pointermove', move); l.removeEventListener('pointerleave', leave); removeEventListener('resize', resize);
      setTimeout(() => { gl?.getExtension('WEBGL_lose_context')?.loseContext(); cv.remove(); }, 1200); // after the loader's fade; the GPU memory back at once
    });
    stop.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    build();
  }
  // a smooth made-up landscape: three octaves of value noise and one soft rise, the same every visit (seed 23)
  function terrain(w, ht) {
    let seed = 23;
    const r = () => { seed = (seed + 0x6d2b79f5) | 0; let x = Math.imul(seed ^ (seed >>> 15), 1 | seed); x = (x + Math.imul(x ^ (x >>> 7), 61 | x)) ^ x; return ((x ^ (x >>> 14)) >>> 0) / 4294967296; };
    const P = new Float32Array(65536);
    for (let i = 0; i < P.length; i++) P[i] = r();
    const sm = (v) => v * v * (3 - 2 * v), g = (a, b) => P[((a & 255) << 8) | (b & 255)];
    const N = (x, y) => {
      const xi = Math.floor(x), yi = Math.floor(y), u = sm(x - xi), v = sm(y - yi);
      const a = g(xi, yi), b = g(xi + 1, yi), c = g(xi, yi + 1), d = g(xi + 1, yi + 1);
      return a + (b - a) * u + (c - a) * v + (a - b - c + d) * u * v;
    };
    return (x, y) => {
      const X = (x / w) * 2.6, Y = (y / w) * 2.6, dx = x / w - 0.3, dy = y / ht - 0.3;
      return N(X, Y) * 0.55 + N(X * 2.03 + 7, Y * 2.03 + 3) * 0.27 + N(X * 4.1 + 1, Y * 4.1 + 9) * 0.13 + Math.exp(-(dx * dx * 2.2 + dy * dy) / 0.06) * 0.45;
    };
  }
  // every second point, then one round of corner cutting: soft curves out of the grid's straight pieces
  function soften(pts, closed) {
    const p = pts.filter((_, i) => i % 2 === 0 || i === pts.length - 1);
    if (p.length < 3) return p;
    const out = [], n = p.length, last = closed ? n : n - 1;
    if (!closed) out.push(p[0]);
    for (let i = 0; i < last; i++) {
      const a = p[i], b = p[(i + 1) % n];
      out.push([a[0] * 0.75 + b[0] * 0.25, a[1] * 0.75 + b[1] * 0.25], [a[0] * 0.25 + b[0] * 0.75, a[1] * 0.25 + b[1] * 0.75]);
    }
    if (!closed) out.push(p[n - 1]);
    return out;
  }
  // the height lines of a field (marching squares on a grid of about 210 cells across), joined into whole lines; handed
  // over a level at a time ({ heavy, lines }), so the work goes in slices between the map's own (null first: the field)
  function* contours(f, w, ht, levels, heavyEvery) {
    const s = Math.max(5, w / 210), cols = Math.ceil(w / s) + 2, rows = Math.ceil(ht / s) + 2, F = new Float32Array(cols * rows);
    let lo = Infinity, hi = -Infinity;
    for (let j = 0; j < rows; j++) for (let i = 0; i < cols; i++) { const v = f(i * s, j * s); F[j * cols + i] = v; if (v < lo) lo = v; if (v > hi) hi = v; }
    yield null;
    for (let k = 1; k < levels; k++) {
      const out = [];
      const at = lo + ((hi - lo) * k) / levels, P = new Map(), segs = [];
      const ep = (key, x1, y1, v1, x2, y2, v2) => { if (!P.has(key)) { const u = (at - v1) / (v2 - v1); P.set(key, [x1 + (x2 - x1) * u, y1 + (y2 - y1) * u]); } return key; };
      for (let j = 0; j < rows - 1; j++) for (let i = 0; i < cols - 1; i++) {
        const a = F[j * cols + i], b = F[j * cols + i + 1], c = F[(j + 1) * cols + i + 1], d = F[(j + 1) * cols + i];
        const idx = (a > at ? 8 : 0) | (b > at ? 4 : 0) | (c > at ? 2 : 0) | (d > at ? 1 : 0);
        if (idx === 0 || idx === 15) continue;
        const x = i * s, y = j * s;
        const T = () => ep((j * cols + i) * 2, x, y, a, x + s, y, b), B = () => ep(((j + 1) * cols + i) * 2, x, y + s, d, x + s, y + s, c);
        const Lf = () => ep((j * cols + i) * 2 + 1, x, y, a, x, y + s, d), R = () => ep((j * cols + i + 1) * 2 + 1, x + s, y, b, x + s, y + s, c);
        switch (idx) {
          case 1: case 14: segs.push([Lf(), B()]); break;
          case 2: case 13: segs.push([B(), R()]); break;
          case 3: case 12: segs.push([Lf(), R()]); break;
          case 4: case 11: segs.push([T(), R()]); break;
          case 6: case 9: segs.push([T(), B()]); break;
          case 7: case 8: segs.push([Lf(), T()]); break;
          case 5: segs.push([T(), R()], [Lf(), B()]); break;
          case 10: segs.push([Lf(), T()], [B(), R()]); break;
        }
      }
      const adj = new Map();
      segs.forEach(([p, q], n) => { for (const e of [p, q]) { if (!adj.has(e)) adj.set(e, []); adj.get(e).push(n); } });
      const used = new Uint8Array(segs.length);
      const walk = (from) => { const o = []; let cur = from; for (;;) { const nx = adj.get(cur).find((m) => !used[m]); if (nx === undefined) return o; used[nx] = 1; const [p, q] = segs[nx]; cur = p === cur ? q : p; o.push(cur); } };
      for (let n = 0; n < segs.length; n++) {
        if (used[n]) continue;
        used[n] = 1;
        const [p, q] = segs[n];
        const keys = walk(p).reverse().concat([p, q], walk(q));
        const closed = keys.length > 3 && keys[0] === keys[keys.length - 1];
        if (closed) keys.pop();
        if (keys.length < 5) continue;
        out.push({ pts: soften(keys.map((e) => P.get(e)), closed), closed });
      }
      yield { heavy: k % heavyEvery === 0, lines: out };
    }
  }
  // a line as quadratic curves between the midpoints of its points
  function trace(p, qs, closed) {
    const n = qs.length;
    if (closed) {
      const m = (i) => { const a = qs[i % n], b = qs[(i + 1) % n]; return [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2]; };
      const s0 = m(0); p.moveTo(s0[0], s0[1]);
      for (let i = 1; i <= n; i++) { const c = qs[i % n], e = m(i); p.quadraticCurveTo(c[0], c[1], e[0], e[1]); }
      p.closePath();
    } else {
      p.moveTo(qs[0][0], qs[0][1]);
      for (let i = 1; i < n - 1; i++) p.quadraticCurveTo(qs[i][0], qs[i][1], (qs[i][0] + qs[i + 1][0]) / 2, (qs[i][1] + qs[i + 1][1]) / 2);
      p.lineTo(qs[n - 1][0], qs[n - 1][1]);
    }
  }

  const f = getComputedStyle(h), s = getComputedStyle(l.querySelector('.ld-words'));
  const fonts = document.fonts?.load ? Promise.all([document.fonts.load(`${f.fontWeight} 40px ${f.fontFamily}`, h.textContent || 'Aa'), document.fonts.load(`${s.fontWeight} 11px ${s.fontFamily}`)]) : Promise.resolve();
  Promise.race([fonts, new Promise((done) => setTimeout(done, 1200))]).catch(() => {}).then(() => {
    l.classList.toggle('ld-solo', look.logo === false || (!mark && !logo.getAttribute('src'))); // nothing to draw first: the name at once
    l.classList.add('is-in');
    if (window.requestIdleCallback) requestIdleCallback(lines, { timeout: 1500 }); else setTimeout(lines, 1); // built when the page has a moment
  });
})();
// VIS-206: a start that failed (js/boot.js, main.js on a lost device): #loader[data-state="failed"][data-reason], the
// words for the reason, and the buttons it allows. Try again = 'map-retry' (boot.js starts a fresh page); Keep waiting
// = 'map-wait' (the watchdog runs again). 'timeout' is not final: the map turning ready (body.ready) clears it.
(() => {
  const fi = document.getElementById('loader')?.dataset.lang === 'fi'; // English or Finnish (the first part above)
  const WORDS = fi ? {
    'no-3d': ['Tämä laite ei pysty näyttämään 3D-karttaa', 'Kartta tarvitsee 3D-grafiikkaa, jota tässä selaimessa ei ole tai se on poistettu käytöstä. Avaa sivu toisella puhelimella tai tietokoneella, tai päivitä selain.', []],
    network: ['Karttaa ei voitu ladata', 'Yhteys katkesi latauksen aikana. Tarkista internetyhteys ja yritä uudelleen.', ['retry']],
    timeout: ['Lataus kestää tavallista kauemmin', 'Yhteys vaikuttaa hitaalta. Voit odottaa vielä tai aloittaa alusta.', ['wait', 'retry']],
    'device-lost': ['Karttaa ei voitu ladata', 'Laitteen grafiikka pysähtyi. Yritä uudelleen.', ['retry']],
    error: ['Karttaa ei voitu ladata', 'Latauksessa meni jokin vikaan. Yritä uudelleen.', ['retry']],
  } : {
    'no-3d': ["This device can't show the 3D map", 'It needs 3D graphics, which this browser does not have or has switched off. Open the page on another phone or computer, or update this browser.', []],
    network: ["The map couldn't load", 'The connection dropped while it was loading. Check your internet, then try again.', ['retry']],
    timeout: ['This is taking longer than usual', 'The connection seems slow. You can keep waiting, or start again.', ['wait', 'retry']],
    'device-lost': ["The map couldn't load", 'The graphics of this device stopped. Try again.', ['retry']],
    error: ["The map couldn't load", 'Something went wrong while it was loading. Try again.', ['retry']],
  };
  const l = document.getElementById('loader'), box = l?.querySelector('.ld-fail');
  if (!box) return;
  if (fi) { box.querySelector('[data-act="wait"]').textContent = 'Odota vielä'; box.querySelector('[data-act="retry"]').textContent = 'Yritä uudelleen'; }
  let before = ''; // the state a 'timeout' covered (the cover's loading pill): back to it when the message goes
  const clear = () => { if (before) l.dataset.state = before; else delete l.dataset.state; delete l.dataset.reason; box.hidden = true; };
  addEventListener('map-failed', (e) => {
    const reason = WORDS[e.detail?.reason] ? e.detail.reason : 'error', [title, text, acts] = WORDS[reason];
    if (l.dataset.state === 'failed' && l.dataset.reason !== 'timeout' && reason === 'timeout') return; // a hard failure stays
    if (l.dataset.state !== 'failed') before = l.dataset.state || '';
    l.dataset.state = 'failed'; l.dataset.reason = reason;
    box.querySelector('h3').textContent = title;
    box.querySelector('p').textContent = text;
    for (const b of box.querySelectorAll('button')) b.hidden = !acts.includes(b.dataset.act);
    box.hidden = false;
  });
  box.addEventListener('click', (e) => {
    const act = e.target.closest?.('button')?.dataset.act;
    if (act === 'wait') { clear(); dispatchEvent(new Event('map-wait')); }
    if (act === 'retry') dispatchEvent(new Event('map-retry'));
  });
  new MutationObserver(() => { if (document.body.classList.contains('ready') && l.dataset.reason === 'timeout') clear(); }).observe(document.body, { attributes: true, attributeFilter: ['class'] });
})();
// VIS-206: the website cover's button (owner's picks 2026-10-21: 1A centred glass button, 2A a phone opens the map full
// window over the hotel's page, with Close).
//   * a click (or Enter) on the cover: 'map-open' (js/boot.js then fetches the map) and one count of "map opened"
//     (a beacon, POST /m/_o/<placement>, worker/views.js: no visitor data); the button turns into the loading pill.
//   * on a touch screen inside a frame, the hotel's page is asked first to show the frame full window (web/consent.js,
//     message 'mapmystay:expand' -> 'mapmystay:expanded'); no answer in 0.6 s = the map opens in a tab of its own
//     (`open=1`: no cover there) and the cover stays.
//   * Close (the top's full screen button turns into it, else .ld-close) or the phone's back gesture: the frame goes back
//     to its box and the cover comes back over the map, which stops drawing (body.covered, main.js off-screen pause);
//     the next press opens it again at once.
(() => {
  const l = document.getElementById('loader'), box = l?.querySelector('.ld-cover');
  if (!box || l.dataset.state !== 'cover') return;
  const meta = (n) => document.querySelector(`meta[name="mapmystay-${n}"]`)?.content || '';
  const framed = window.parent !== window, touch = matchMedia('(pointer: coarse)').matches;
  const fi = l.dataset.lang === 'fi';
  const close = document.querySelector('.ld-close');
  if (close && fi) close.textContent = 'Sulje';
  // the hotel's page: the only origin the cover talks to (the frame may only sit on the client's listed websites)
  let host = '';
  try { host = location.ancestorOrigins?.[0] || (document.referrer && new URL(document.referrer).origin) || ''; } catch { /* errors:ignore: no usable referrer = no full window, a tab instead */ }
  let started = false, asked = null;
  const ask = (type) => new Promise((done) => {
    if (!host) return done(false);
    asked = done;
    window.parent.postMessage({ type }, host);
    setTimeout(() => { if (asked === done) { asked = null; done(false); } }, 600);
  });
  addEventListener('message', (e) => {
    if (e.source !== window.parent || e.origin !== host || !e.data || typeof e.data !== 'object') return;
    if (e.data.type === 'mapmystay:expanded' && asked) { const a = asked; asked = null; a(true); }
    if (e.data.type === 'mapmystay:collapsed') fold(); // the back gesture on the hotel's page
  });
  const fold = () => {
    delete document.documentElement.dataset.expanded;
    if (!started) return;
    document.body.classList.add('covered'); // css: the cover over the map again; main.js: nothing drawn meanwhile
    l.dataset.state = 'cover';
  };
  addEventListener('map-collapse', () => { if (framed && host) window.parent.postMessage({ type: 'mapmystay:collapse' }, host); fold(); });
  close?.addEventListener('click', () => dispatchEvent(new Event('map-collapse')));
  async function open() {
    if (l.dataset.state !== 'cover') return;
    if (framed && touch) {
      if (!(await ask('mapmystay:expand'))) { // the hotel's page cannot show it full window: a tab of its own
        const u = new URL(location.href); u.searchParams.set('open', '1');
        window.open(u.href, '_blank', 'noopener');
        return;
      }
      document.documentElement.dataset.expanded = '1';
    }
    if (started) { document.body.classList.remove('covered'); delete l.dataset.state; return; } // opened before: back at once
    started = true;
    l.dataset.state = 'opening';
    box.querySelector('.ld-opening').hidden = false;
    const plc = meta('placement');
    if (/^plc_[0-9a-z]+$/.test(plc)) try { navigator.sendBeacon(`/m/_o/${plc}`); } catch { /* errors:ignore: a count never stops the map */ }
    dispatchEvent(new Event('map-open'));
  }
  l.addEventListener('click', (e) => { if (!e.target.closest('.ld-fail')) open(); }); // the whole picture, and the button (Enter, Space)
})();
